import type { ReactNode } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLanguage } from "@/i18n/LanguageContext";
import { CAL_COLORS, type CalColor } from "@/lib/retailCalendarData";

export const CAL_DOT: Record<CalColor, string> = {
  red: "bg-cal-red", orange: "bg-cal-orange", gold: "bg-cal-gold", green: "bg-cal-green",
  blue: "bg-cal-blue", violet: "bg-cal-violet", teal: "bg-cal-teal", graphite: "bg-cal-graphite",
};

interface Props {
  value: CalColor;
  onChange: (c: CalColor) => void;
  trigger: ReactNode;
  /** Nombre editable del color (opcional). */
  name?: string | undefined;
  onNameChange?: (v: string) => void;
  onRemove?: () => void;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

const SIZE = 196;
const R = 76;

/** Rueda cromática calibrada: 8 tonos de prioridad en anillo, sin ventana modal. */
export function CalendarColorWheel({ value, onChange, trigger, name, onNameChange, onRemove, open, onOpenChange }: Props) {
  const { t } = useLanguage();
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" className="w-[248px] rounded-2xl border-border/70 p-4 shadow-[0_18px_40px_-18px_hsl(var(--foreground)/0.35)]">
        <p className="text-xs font-semibold">{t("retailCal", "pickTone")}</p>
        <div className="relative mx-auto mt-2" style={{ width: SIZE, height: SIZE }} role="radiogroup" aria-label={t("retailCal", "pickTone")}>
          <div className="pointer-events-none absolute inset-[30px] rounded-full border border-border/60" aria-hidden />
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center" aria-hidden>
            <span className={`h-12 w-12 rounded-full shadow-inner ${CAL_DOT[value]}`} />
            <span className="mt-1.5 text-[11px] font-semibold text-muted-foreground">{t("retailCal", `color_${value}`)}</span>
          </div>
          {CAL_COLORS.map((c, i) => {
            const a = (i / CAL_COLORS.length) * Math.PI * 2 - Math.PI / 2;
            const x = SIZE / 2 + R * Math.cos(a) - 22;
            const y = SIZE / 2 + R * Math.sin(a) - 22;
            const on = value === c;
            return (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={on}
                aria-label={t("retailCal", `color_${c}`)}
                title={t("retailCal", `color_${c}`)}
                onClick={() => onChange(c)}
                className="absolute z-10 flex h-11 w-11 touch-manipulation items-center justify-center rounded-full transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/40"
                style={{ left: x, top: y }}
              >
                <span className={`pointer-events-none rounded-full transition-all ${CAL_DOT[c]} ${on ? "h-8 w-8 ring-2 ring-foreground ring-offset-2 ring-offset-background" : "h-6 w-6"}`} />
              </button>
            );
          })}
        </div>
        {onNameChange && (
          <label className="mt-2 block text-xs font-semibold">
            {t("retailCal", "colorName")}
            <input
              value={name ?? ""}
              maxLength={40}
              placeholder={t("retailCal", "colorNamePh")}
              onChange={(e) => onNameChange(e.target.value)}
              className="mt-1 w-full min-h-11 rounded-xl border border-border/80 bg-background px-3 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-foreground/20"
            />
          </label>
        )}
        <div className="mt-3 flex items-center justify-between">
          {onRemove ? (
            <button type="button" onClick={onRemove} className="min-h-11 px-1 text-xs font-semibold text-muted-foreground hover:text-foreground">{t("retailCal", "colorRemove")}</button>
          ) : <span />}
          <button type="button" onClick={() => onOpenChange(false)} className="min-h-11 rounded-full bg-foreground px-4 text-xs font-semibold text-background">{t("retailCal", "colorDone")}</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
